import { useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { fmtMoney, fmtDate, todayISO } from "@/lib/format";
import { printPLReport } from "@/lib/print";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

function rangeFor(period) {
  const now = new Date();
  const end = todayISO();
  let start = new Date(now);
  if (period === "weekly") start.setDate(now.getDate() - 6);
  else if (period === "monthly") start = new Date(now.getFullYear(), now.getMonth(), 1);
  else if (period === "yearly") start = new Date(now.getFullYear(), 0, 1);
  return { start: start.toISOString().slice(0, 10), end };
}

const PERIODS = [
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "yearly", label: "Yearly" },
  { key: "custom", label: "Custom" },
];

export default function Reports() {
  const { settings, business, currency, dateFormat } = useSettings();
  const [period, setPeriod] = useState("monthly");
  const [custom, setCustom] = useState({ start: rangeFor("monthly").start, end: todayISO() });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = useCallback(async (p) => {
    const active = p || period;
    const r = active === "custom" ? custom : rangeFor(active);
    setLoading(true);
    try {
      const res = await api.get("/reports/pl", { params: r });
      setReport(res.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [period, custom]);

  const periodLabel = PERIODS.find((x) => x.key === period)?.label || "Custom";

  // READ-ONLY graph data from the existing P&L response.
  // No FinLend financial calculation is changed here.
  const dailyRecords = (report?.daily_records || []).map((row, index) => ({
    ...row,
    chartDate:
      row.date ||
      row.day ||
      row.date_key ||
      `Day ${index + 1}`,
    money_lent: Number(row.money_lent || 0),
    repayments_received: Number(row.repayments_received || 0),
    principal_collected: Number(row.principal_collected || 0),
    interest_collected: Number(row.interest_collected || 0),
    expenses: Number(row.expenses || 0),
    cash_collected: Number(row.cash_collected || 0),
    upi_collected: Number(row.upi_collected || 0),
    net_profit_loss: Number(row.net_profit_loss || 0),
  }));

  const chartMoney = (value) =>
    fmtMoney(Number(value || 0), currency);

  const metric = (label, value, tone) => (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-xl font-bold ${tone || "text-navy"}`}>{fmtMoney(value, currency)}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Profit &amp; Loss Reports</h1>
        <p className="text-slate-500 mt-1">Analyze your lending performance</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
        <div className="flex flex-wrap gap-2">
          {PERIODS.map((p) => (
            <button key={p.key} onClick={() => { setPeriod(p.key); if (p.key !== "custom") generate(p.key); }}
              data-testid={`profit-loss-filter-${p.key}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${period === p.key ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {p.label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex flex-wrap items-end gap-3">
            <div><Label>Start Date</Label>
              <Input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} data-testid="report-start-date" /></div>
            <div><Label>End Date</Label>
              <Input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} data-testid="report-end-date" /></div>
            <Button className="bg-navy hover:bg-navy-hover" onClick={() => generate("custom")} data-testid="generate-report-button">Generate</Button>
          </div>
        )}
      </div>

      {loading && <p className="text-slate-400">Generating…</p>}

      {report && !loading && (
        <div className="space-y-6" data-testid="report-result">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">Period: <b className="text-navy">{fmtDate(report.start, dateFormat)} – {fmtDate(report.end, dateFormat)}</b></p>
            <Button variant="outline" onClick={() => printPLReport(report, periodLabel, business, settings)} data-testid="print-pl-report-button">
              <Printer className="h-4 w-4 mr-2" /> Print / Save PDF
            </Button>
          </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {metric("Total Money Lent", report.total_lent)}
              {metric("Repayments Received", report.repayments_received, "text-emerald-700")}
              {metric("Interest / Profit", report.interest_profit, "text-maroon")}

              {metric("Principal Collected", report.principal_collected)}
              {metric("Interest Collected", report.interest_collected, "text-maroon")}
              {metric("Today's Collection", report.today_collection, "text-emerald-700")}

              {metric("Weekly Collection", report.weekly_collection, "text-emerald-700")}
              {metric("Cash Collected", report.cash_collected, "text-emerald-700")}
              {metric("UPI Collected", report.upi_collected, "text-emerald-700")}

              {metric("Principal Outstanding", report.principal_outstanding)}
              {metric("Interest Outstanding", report.interest_outstanding, "text-maroon")}
              {metric("Outstanding Balance", report.outstanding_balance)}

              {metric("Overdue Balance", report.overdue_balance, "text-red-600")}
              {metric("Total Expenses", report.total_expenses, "text-red-600")}
            </div>

          {dailyRecords.length > 0 && (
            <div className="space-y-6">

              {/* MONEY LENT VS REPAYMENTS */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <h2 className="font-heading text-lg font-bold text-navy">
                  Money Lent vs Repayments
                </h2>
                <p className="text-sm text-slate-500 mb-4">
                  Daily movement during the selected period
                </p>

                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dailyRecords}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="chartDate" />
                      <YAxis />
                      <Tooltip formatter={(value) => chartMoney(value)} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="money_lent"
                        name="Money Lent"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="repayments_received"
                        name="Repayments Received"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* PRINCIPAL VS INTEREST */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <h2 className="font-heading text-lg font-bold text-navy">
                  Principal vs Interest Collected
                </h2>
                <p className="text-sm text-slate-500 mb-4">
                  Daily collection split
                </p>

                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyRecords}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="chartDate" />
                      <YAxis />
                      <Tooltip formatter={(value) => chartMoney(value)} />
                      <Legend />
                      <Bar
                        dataKey="principal_collected"
                        name="Principal"
                        stackId="collection"
                      />
                      <Bar
                        dataKey="interest_collected"
                        name="Interest"
                        stackId="collection"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* DAILY PROFIT / LOSS */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <h2 className="font-heading text-lg font-bold text-navy">
                  Daily Profit / Loss
                </h2>
                <p className="text-sm text-slate-500 mb-4">
                  Interest collected minus expenses
                </p>

                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dailyRecords}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="chartDate" />
                      <YAxis />
                      <Tooltip formatter={(value) => chartMoney(value)} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="net_profit_loss"
                        name="Net Profit / Loss"
                        strokeWidth={3}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* CASH VS UPI */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <h2 className="font-heading text-lg font-bold text-navy">
                  Cash vs UPI Collection
                </h2>
                <p className="text-sm text-slate-500 mb-4">
                  Daily collection method breakdown
                </p>

                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyRecords}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="chartDate" />
                      <YAxis />
                      <Tooltip formatter={(value) => chartMoney(value)} />
                      <Legend />
                      <Bar dataKey="cash_collected" name="Cash" />
                      <Bar dataKey="upi_collected" name="UPI" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* DAILY EXPENSES */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <h2 className="font-heading text-lg font-bold text-navy">
                  Daily Expenses
                </h2>
                <p className="text-sm text-slate-500 mb-4">
                  Expenses recorded during the selected period
                </p>

                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dailyRecords}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="chartDate" />
                      <YAxis />
                      <Tooltip formatter={(value) => chartMoney(value)} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="expenses"
                        name="Expenses"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

            </div>
          )}

          <div className={`rounded-xl border p-6 shadow-sm ${report.net_profit_loss >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Net Profit / Loss</div>
                <div className={`mt-1 font-mono text-3xl font-bold ${report.net_profit_loss >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                  {fmtMoney(report.net_profit_loss, currency)}
                </div>
                <div className="text-xs text-slate-500 mt-1">Interest Profit − Expenses</div>
              </div>
              {report.net_profit_loss >= 0
                ? <TrendingUp className="h-12 w-12 text-emerald-500" />
                : <TrendingDown className="h-12 w-12 text-red-500" />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
